/**
 * @fileoverview Membership & Share Capital — Member Count (5-Year Trend) page.
 * @description Ported from the InsightBox Banking design system's own
 * `MembershipShareCapital/MemberCount.tsx`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  ALL_BRANCHES_ID,
  BranchFilterSelect,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  MemberMovementChart,
  branchLabel,
  core,
  formatNumber,
  formatSignedPct,
  isHighestIn5Y,
  memberSeries,
  monthlyMemberMovement,
  resolveBankingAccessScope,
  resolveFy,
  useBankingData,
  yoyGrowth,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'

/**
 * @component
 */
function BankingMembershipMemberCountPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.membershipMemberCount.kicker')}
      title={t('banking.membershipMemberCount.title')}
      description={t('banking.membershipMemberCount.description', {
        scope:
          branchId === ALL_BRANCHES_ID
            ? t('banking.membershipMemberCount.theBanks')
            : t('banking.membershipMemberCount.branchPossessive', {
                branch: branchLabel(branchId),
              }),
      })}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const is5Years = fyFilter === FIVE_YEAR_ID
  const fy = resolveFy(fyFilter)

  // Always the full 5 years — feeds both the KPI cards' annual figures and the "5 Years"-only
  // stats below, regardless of what the chart shows.
  const fullSeries = memberSeries(data, branchId)
  const current = fullSeries.find((s) => s.fy === fy)!
  // "5 Years" plots the FY21->FY25 movement; an individual year drills into that year's own
  // Apr->Mar monthly movement instead of a single lonely point.
  const chartSeries = is5Years ? fullSeries : monthlyMemberMovement(data, fy, branchId)
  const netChange = current.added - current.cancelled
  const memberYoY = yoyGrowth(fy, (f) => core(data, f, branchId).totalMembers)

  // New Members Added / Cancellations are genuine flow metrics (how many joined/left DURING each
  // year), so "5 Years" sums them across FY21-FY25. Total Members is a stock — a headcount at a
  // point in time — so it stays FY25's figure rather than an added-up (meaningless) total.
  const sumAdded = fullSeries.reduce((sum, s) => sum + s.added, 0)
  const sumCancelled = fullSeries.reduce((sum, s) => sum + s.cancelled, 0)
  const sumNetChange = sumAdded - sumCancelled

  // The chart's growth badge must equal the same "5Y Sum" the KPI card shows — not FY25's total
  // minus FY21's total — since FY21's own added/cancelled (an authored starting point, not tied to
  // any real prior year) are part of that sum too.
  const netGrowth5yPct = (sumNetChange / fullSeries[0]!.total) * 100
  const highestIn5Y = isHighestIn5Y(fy, (f) => core(data, f, branchId).totalMembers)

  const growthBadge = is5Years
    ? {
        positive: sumNetChange >= 0,
        text: t('banking.membershipMemberCount.growthBadge5Y', {
          pct: formatSignedPct(netGrowth5yPct),
          sign: sumNetChange >= 0 ? '+' : '',
          count: formatNumber(sumNetChange),
        }),
      }
    : memberYoY !== null
      ? {
          positive: memberYoY >= 0,
          text: t('banking.membershipMemberCount.growthBadgeYear', {
            value: formatSignedPct(memberYoY),
          }),
        }
      : null

  const kpis: BankingKpiStripItem[] = is5Years
    ? [
        {
          label: t('banking.membershipMemberCount.kpi.totalMembers'),
          value: formatNumber(current.total),
          sublabel: highestIn5Y
            ? t('banking.membershipMemberCount.kpi.highestIn5Y')
            : t('banking.membershipMemberCount.kpi.asOfFy25'),
          highlight: true,
        },
        {
          label: t('banking.membershipMemberCount.kpi.newMembers5Y'),
          value: formatNumber(sumAdded),
          sublabel: t('banking.membershipMemberCount.kpi.fiveYearCombined'),
        },
        {
          label: t('banking.membershipMemberCount.kpi.cancellations5Y'),
          value: formatNumber(sumCancelled),
          sublabel: t('banking.membershipMemberCount.kpi.fiveYearCombined'),
        },
        {
          label: t('banking.membershipMemberCount.kpi.netChange5Y'),
          value: `${sumNetChange >= 0 ? '+' : ''}${formatNumber(sumNetChange)}`,
          sublabel: t('banking.membershipMemberCount.kpi.fiveYearCombined'),
        },
      ]
    : [
        {
          label: t('banking.membershipMemberCount.kpi.totalMembers'),
          value: formatNumber(current.total),
          sublabel: fy,
          highlight: true,
        },
        {
          label: t('banking.membershipMemberCount.kpi.newMembers'),
          value: formatNumber(current.added),
          sublabel: fy,
        },
        {
          label: t('banking.membershipMemberCount.kpi.cancellations'),
          value: formatNumber(current.cancelled),
          sublabel: fy,
        },
        {
          label: t('banking.membershipMemberCount.kpi.netChange'),
          value: `${netChange >= 0 ? '+' : ''}${formatNumber(netChange)}`,
          sublabel:
            memberYoY !== null
              ? t('banking.membershipMemberCount.kpi.growthSuffix', {
                  value: formatSignedPct(memberYoY),
                })
              : t('banking.baseYear'),
        },
      ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        fill
        title={
          is5Years
            ? t('banking.membershipMemberCount.chartTitleFiveYear')
            : t('banking.membershipMemberCount.chartTitleMonthly', { fy })
        }
        subtitle={
          is5Years
            ? t('banking.membershipMemberCount.chartSubtitleFiveYear', {
                branch: branchLabel(branchId),
              })
            : t('banking.membershipMemberCount.chartSubtitleMonthly', {
                branch: branchLabel(branchId),
              })
        }
        action={
          growthBadge ? (
            <span
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
                growthBadge.positive ? 'bg-green-50 text-success' : 'bg-red-50 text-danger'
              }`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path
                  d={growthBadge.positive ? 'M12 19V5M5 12l7-7 7 7' : 'M12 5v14M5 12l7 7 7-7'}
                />
              </svg>
              {growthBadge.text}
            </span>
          ) : undefined
        }
      >
        <div className="relative min-h-0 flex-1">
          {/* Keyed on the view mode — see TrendArea's own file comment for why. */}
          <MemberMovementChart key={is5Years ? '5y' : 'monthly'} data={chartSeries} height="100%" />
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={
          is5Years
            ? t('banking.membershipMemberCount.summaryTitleFiveYear')
            : t('banking.membershipMemberCount.summaryTitleYear', { fy })
        }
        lines={
          is5Years
            ? [
                t('banking.membershipMemberCount.summary.fiveYearLine1', {
                  added: formatNumber(sumAdded),
                  cancelled: formatNumber(sumCancelled),
                  net: formatNumber(sumNetChange),
                }),
                t('banking.membershipMemberCount.summary.fiveYearLine2', {
                  total: formatNumber(current.total),
                  highestClause: highestIn5Y
                    ? t('banking.membershipMemberCount.summary.highestClause')
                    : '',
                }),
              ]
            : [
                t('banking.membershipMemberCount.summary.yearLine1', {
                  direction:
                    netChange >= 0
                      ? t('banking.membershipMemberCount.summary.increased')
                      : t('banking.membershipMemberCount.summary.decreased'),
                  amount: formatNumber(Math.abs(netChange)),
                  fy,
                  added: formatNumber(current.added),
                  cancelled: formatNumber(current.cancelled),
                }),
                t('banking.membershipMemberCount.summary.yearLine2', {
                  fy,
                  total: formatNumber(current.total),
                  highestClause: highestIn5Y
                    ? t('banking.membershipMemberCount.summary.highestClause')
                    : '',
                }),
              ]
        }
      />
    </div>
  )
}

export default BankingMembershipMemberCountPage
